import {
  Typography,
  Box,
  Card,
  CardContent,
  Container,
  alpha,
  useTheme,
  Chip,
  Button,
  Alert,
  Divider,
  Stack,
} from "@mui/material";
import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFlashcard, clearFlashcard } from "../../state/slices/flashcard";
import { checkFlashcardProgress } from "../../state/slices/flashcardProgress";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import { School, Edit, Visibility, Collections, ArrowBack, CheckCircle, Lightbulb } from "@mui/icons-material";

const FlashcardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentFlashcard, loading } = useSelector((state) => state.flashcard);
  const { flashcardProgress } = useSelector((state) => state.flashcardProgress);
  const user = useSelector((state) => state.user.user);
  const isTutor = user?.roles?.includes("TUTOR");
  const isStudent = user?.roles?.includes("STUDENT") || user?.roles?.includes("PARENT");
  const isCreator = currentFlashcard?.createdBy?._id === user?.id || currentFlashcard?.createdBy === user?.id;
  
  // Root navigation paths - don't show back button on these
  const rootPaths = ['/quiz/my', '/quiz/student', '/flashcard/my', '/flashcard/student', '/progress', '/', '/request', '/student-request', '/tutor-class-room', '/student-class-room'];
  const showBackButton = !rootPaths.includes(location.pathname);

  useEffect(() => {
    dispatch(getFlashcard(id));
    // Only check progress for students/parents
    if (isStudent) {
      dispatch(checkFlashcardProgress(id));
    }
    return () => {
      dispatch(clearFlashcard());
    };
  }, [id, dispatch, isStudent]);

  if (loading || !currentFlashcard) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Back Button - only show if not on root navigation page */}
        {showBackButton && (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {t('common.back')}
          </Button>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: 'white',
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            overflow: 'hidden',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              p: 4,
              bgcolor: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexGrow: 1 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Collections sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                    {currentFlashcard.title}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    {currentFlashcard.subject && (
                      <Chip 
                        label={currentFlashcard.subject}
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {currentFlashcard.gradeLevel && (
                      <Chip 
                        label={currentFlashcard.gradeLevel}
                        sx={{ 
                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                          color: theme.palette.secondary.main,
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Collections sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {currentFlashcard.cards?.length || 0} {t('flashcard.cards')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                {/* Only show Study/Restudy buttons for students/parents */}
                {isStudent && (
                  flashcardProgress?.allMastered ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<School />}
                        onClick={() => navigate(`/flashcard/study/${id}?review=true`)}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}
                      >
                        {t('flashcard.restudy')}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => {
                          // Show progress details
                          alert(`${t('flashcard.alreadyMastered')}\n${t('flashcard.masteredCards')}: ${flashcardProgress.progress?.masteredCards}/${flashcardProgress.progress?.totalCards}`);
                        }}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                        }}
                      >
                        {t('flashcard.viewProgress')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<School />}
                      onClick={() => navigate(`/flashcard/study/${id}`)}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      }}
                    >
                      {t('flashcard.study')}
                    </Button>
                  )
                )}
                {isCreator && (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/flashcard/create`, { state: { flashcard: currentFlashcard } })}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    {t('flashcard.edit')}
                  </Button>
                )}
              </Box>
            </Box>

            {flashcardProgress?.allMastered && (
              <Alert 
                severity="success" 
                icon={<CheckCircle />}
                sx={{ 
                  borderRadius: 2,
                }}
              >
                {t('flashcard.alreadyMastered')} {flashcardProgress.progress?.lastStudied && (
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    {t('flashcard.lastStudied')}: {new Date(flashcardProgress.progress.lastStudied).toLocaleDateString()}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>

          <Divider />

          {/* Cards Preview Section */}
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Lightbulb sx={{ fontSize: 24, color: theme.palette.primary.main }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {t('flashcard.cardsPreview')}
              </Typography>
            </Box>
            {currentFlashcard.cards?.map((card, index) => (
              <Card 
                key={index} 
                elevation={0}
                sx={{ 
                  mb: 2, 
                  p: 3,
                  borderRadius: 2,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('flashcard.card')} {index + 1}
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        mb: 1,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {t('flashcard.front')}:
                      </Typography>
                      <Typography variant="body1">
                        {card.front}
                      </Typography>
                    </Box>
                    {card.hint && (
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.secondary.main, 0.05),
                          mt: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {t('flashcard.hint')}:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {card.hint}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Card>
            ))}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default FlashcardDetail;

